import { Input } from '@/components/ui/input.js';
import { Eye, EyeOff, CheckCircle2, Box } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button.js';
import { usePostRequest } from '@/shared/hooks/requests.js';
import { AUTH_LOGIN, AUTH_SIGNUP } from '@/shared/utils/urls.js';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import VersionBadge from '@/components/VersionBadge.jsx';

export const Authorization = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [signType, setSignType] = useState('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({});

  const authSignin = usePostRequest({ url: AUTH_LOGIN });
  const authSignup = usePostRequest({ url: AUTH_SIGNUP });

  const handleSubmit = async (e) => {
    e.preventDefault();

    let newErrors = {};
    if (!email) {
      newErrors.email = t('auth.errors.emailRequired');
    }
    if (!password) {
      newErrors.password = t('auth.errors.passwordRequired');
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      if (signType === 'signin') {
        const { response, success } = await authSignin.request({
          data: { email, password },
        });
        if (success) {
          toast.success(t('auth.toasts.loginSuccess'));
          localStorage.setItem('userType', response?.user.userType);
          localStorage.setItem('token', response?.tokens.accessToken);
          if (response?.user?.id) {
            localStorage.setItem('userId', response.user.id);
          }
          if (response?.user?.name) {
            localStorage.setItem('userName', response.user.name);
          }
          if (response?.user?.email) {
            localStorage.setItem('userEmail', response.user.email);
          }
          navigate('/projects', { replace: true });
        } else {
          toast.error(t('auth.toasts.loginError'));
        }
      } else {
        const { response, success } = await authSignup.request({
          data: { email, password, user_type: 'admin' },
        });

        if (success) {
          toast.success(t('auth.toasts.signupSuccess'));
          localStorage.setItem('userType', response?.user.userType);
          localStorage.setItem('token', response?.tokens.accessToken);
          localStorage.setItem('refreshToken', response?.tokens.refreshToken);
          if (response?.user?.id) {
            localStorage.setItem('userId', response.user.id);
          }
          if (response?.user?.name) {
            localStorage.setItem('userName', response.user.name);
          }
          if (response?.user?.email) {
            localStorage.setItem('userEmail', response.user.email);
          }
          navigate('/projects', { replace: true });
        } else {
          toast.error(t('auth.toasts.signupError'));
        }
      }
    }
  };

  return (
    <div className="w-full min-h-dvh flex items-center justify-center bg-background relative max-md:p-4">
      <div className="flex flex-col md:flex-row w-full max-w-4xl md:h-[600px] shadow-2xl  rounded-xl overflow-hidden">
        {/* Left Side - Info Section (marketing pane — the form is the point
            on a phone, so it only appears from md up) */}
        <div
          className="hidden md:block md:w-1/2 p-12 relative overflow-hidden"
          style={{ background: 'radial-gradient(130% 130% at 18% 0%, #eaf1ff, #f6f8fe 68%)' }}
        >
          {/* A drifting aurora bloom — the brand moment, kept soft on a light pane. */}
          <div
            aria-hidden
            className="aurora-orb pointer-events-none absolute -right-24 -top-28 h-[360px] w-[360px] rounded-full opacity-40"
            style={{ filter: 'blur(64px)' }}
          />

          {/* Content */}
          <div className="relative z-10">
            {/* Logo */}
            <div className="flex items-center gap-2 mb-8">
              <img src="/logo-docs.svg" alt="logo" className="w-[150px]" />
            </div>

            {/* Headline */}
            <h1 className="text-[24px] font-bold text-center mt-[60px] mb-[40px]">
              {t('auth.headline')}
            </h1>

            {/* Features */}
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 flex-shrink-0 mt-0.5" color='#6FA4F9' />
                <p className="text-[#666]">
                  {t('auth.features.ai')}
                </p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 flex-shrink-0 mt-0.5" color='#6FA4F9' />
                <p className="text-[#666]">
                  {t('auth.features.sync')}
                </p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 flex-shrink-0 mt-0.5" color='#6FA4F9' />
                <p className="text-[#666]">
                  {t('auth.features.security')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Form Section */}
        <div className="w-full md:w-1/2 bg-white p-6 sm:p-8 md:p-12 flex flex-col justify-center">
          {/* Tabs */}
          <div className="flex gap-4 mb-8">
            <button
              onClick={() => setSignType('signin')}
              className={`px-6 py-2 rounded-lg font-medium transition-all cursor-pointer ${signType === 'signin'
                ? 'bg-gray-100 text-gray-900'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              {t('auth.signIn')}
            </button>
            <button
              onClick={() => setSignType('signup')}
              className={`px-6 py-2 rounded-lg font-medium transition-all cursor-pointer ${signType === 'signup'
                ? 'bg-gray-100 text-gray-900'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              {t('auth.signUp')}
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div>
              <label className="text-sm text-gray-600 mb-2 block">
                {t('auth.emailLabel')}
              </label>
              <Input
                type="email"
                placeholder="uzinfocom@gmail.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`h-12 ${errors.email ? 'border-red-500' : ''}`}
              />
              {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
            </div>

            {/* Password */}
            <div>
              <label className="text-sm text-gray-600 mb-2 block">{t('auth.passwordLabel')}</label>
              <div className="relative">
                <Input
                  type={show ? 'text' : 'password'}
                  placeholder="••••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`h-12 pr-10 ${errors.password ? 'border-red-500' : ''}`}
                />
                <button
                  type="button"
                  onClick={() => setShow(!show)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {show ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.password && <p className="text-xs text-red-500 mt-1">{errors.password}</p>}
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full h-12 text-white font-medium rounded-lg transition-all cursor-pointer"
              style={{
                background: 'linear-gradient(135deg, #4185F6 0%, #6FA4F9 100%)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #3570E0 0%, #5E8FE0 100%)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #4185F6 0%, #6FA4F9 100%)';
              }}
              disabled={authSignin.loading || authSignup.loading}
            >
              {authSignin.loading || authSignup.loading
                ? t('auth.loading')
                : signType === 'signin'
                  ? t('auth.submitSignIn')
                  : t('auth.signUp')}
            </Button>
          </form>

          {/* Footer Links */}
          <div className="mt-8 text-center text-xs text-gray-500">
            <Trans
              i18nKey="auth.footerAgreement"
              components={{
                termsLink: <a href="#" className="text-blue-500 hover:underline" />,
                agreementLink: <a href="#" className="text-blue-500 hover:underline" />,
                privacyLink: <a href="#" className="text-blue-500 hover:underline" />,
              }}
            />
          </div>
        </div>
      </div>
      <div className="absolute bottom-4 right-4">
        <VersionBadge />
      </div>
    </div>
  );
};
